import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  CreatePostInput,
  CreatePostOrderInput,
  Post,
  PostOrder,
  PostType,
  SiteStatus,
  SiteType,
  UpdateSiteInput,
} from '@dsr/shared-types';
import {
  useCreatePost,
  useCreatePostOrder,
  useDeleteSite,
  usePostOrders,
  usePosts,
  usePublishPostOrder,
  useSite,
  useUpdateSite,
} from '../api/queries/sites';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  StatusBadge,
  Table,
  Textarea,
} from '../components/ui';
import { can } from '../auth/store';
import { ApiError } from '../api/client';

const SITE_TYPES: SiteType[] = [
  'fixed_post',
  'patrol',
  'residential',
  'commercial',
  'event_venue',
  'construction',
  'retail',
];
const SITE_STATUSES: SiteStatus[] = ['active', 'inactive', 'survey'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV',
  'NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN',
  'TX','UT','VT','VA','WA','WV','WI','WY','DC','PR','VI','GU','AS','MP',
] as const;
const POST_TYPES: PostType[] = [
  'gate',
  'lobby',
  'roving',
  'control_room',
  'dock',
  'perimeter',
];

function fmtDate(epoch: number | null): string {
  if (epoch === null) return '—';
  return new Date(epoch).toLocaleDateString('en-US');
}

export function SiteDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const siteId = id ? Number(id) : null;

  const { data, isLoading, isError, error } = useSite(siteId);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteSite();

  const canWrite = can('site.write');
  const canDelete = can('site.delete');

  if (isLoading) return <p className="muted">Loading…</p>;
  if (isError || !data) {
    return (
      <p className="error-text" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load site.'}
      </p>
    );
  }

  const site = data.site;

  const onDelete = async () => {
    if (siteId === null) return;
    try {
      await del.mutateAsync(siteId);
      toast.success('Site deleted.');
      setConfirmDelete(false);
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to delete site.');
    }
  };

  return (
    <div className="page site-detail-page">
      <nav className="breadcrumbs">
        <Link to="/sites">Sites</Link>
        <span aria-hidden="true"> / </span>
        <span>{site.name}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1>{site.name}</h1>
          <p className="page-sub">
            <StatusBadge status={site.status}>{site.status}</StatusBadge>{' '}
            {site.site_type}
          </p>
        </div>
        <div className="header-actions">
          {canWrite && (
            <Button variant="ghost" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </header>

      <section className="detail-card">
        <h2>Overview</h2>
        <dl className="detail-grid">
          <div>
            <dt>Client</dt>
            <dd>
              <Link to={`/organizations/${site.organization_id}`}>
                #{site.organization_id}
              </Link>
            </dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{site.site_type}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{site.status}</dd>
          </div>
          <div>
            <dt>Geofence radius</dt>
            <dd>{site.geofence_radius_m} m</dd>
          </div>
          <div className="detail-grid-full">
            <dt>Address</dt>
            <dd>{site.address ?? '—'}</dd>
          </div>
          <div>
            <dt>State</dt>
            <dd>{site.state ?? '—'}</dd>
          </div>
          <div>
            <dt>Coordinates</dt>
            <dd>
              {site.lat && site.lng ? `${site.lat}, ${site.lng}` : '—'}
            </dd>
          </div>
          <div className="detail-grid-full">
            <dt>Notes</dt>
            <dd>{site.notes ?? '—'}</dd>
          </div>
        </dl>
      </section>

      <PostsSection siteId={site.id} canWrite={canWrite} />

      {editOpen && (
        <EditSiteModal
          siteId={site.id}
          initial={{
            name: site.name,
            address: site.address,
            state: site.state,
            lat: site.lat,
            lng: site.lng,
            geofence_radius_m: site.geofence_radius_m,
            site_type: site.site_type,
            status: site.status,
            notes: site.notes,
          }}
          onClose={() => setEditOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete site"
        message={`Delete "${site.name}"? This soft-deletes the site.`}
        confirmLabel="Delete"
        danger
        busy={del.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Posts + per-post post orders
// ---------------------------------------------------------------------------

function PostsSection({
  siteId,
  canWrite,
}: {
  siteId: number;
  canWrite: boolean;
}): JSX.Element {
  const { data, isLoading } = usePosts(siteId);
  const [addOpen, setAddOpen] = useState(false);
  const posts = data?.posts ?? [];

  return (
    <section className="detail-card">
      <div className="section-header">
        <h2>Posts</h2>
        {canWrite && (
          <Button variant="ghost" className="btn-sm" onClick={() => setAddOpen(true)}>
            Add post
          </Button>
        )}
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && posts.length === 0 && (
        <p className="muted">No posts defined for this site.</p>
      )}

      {posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}

      {addOpen && <AddPostModal siteId={siteId} onClose={() => setAddOpen(false)} />}
    </section>
  );
}

function PostCard({ post }: { post: Post }): JSX.Element {
  const [ordersOpen, setOrdersOpen] = useState(false);

  return (
    <div className="post-card">
      <div className="post-card-head">
        <div>
          <strong>{post.name}</strong>{' '}
          <Badge tone="info">{post.post_type}</Badge>
          <div className="muted small">
            {post.coverage_hours ?? 'No coverage hours set'} ·{' '}
            {post.headcount_required} required
          </div>
        </div>
        <Button variant="ghost" className="btn-sm" onClick={() => setOrdersOpen((v) => !v)}>
          {ordersOpen ? 'Hide post orders' : 'Post orders'}
        </Button>
      </div>
      {ordersOpen && <PostOrdersPanel postId={post.id} />}
    </div>
  );
}

function PostOrdersPanel({ postId }: { postId: number }): JSX.Element {
  const { data, isLoading } = usePostOrders(postId);
  const [draftOpen, setDraftOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const canWriteOrders = can('post_order.write');
  const canPublish = can('post_order.publish');

  const orders = data?.post_orders ?? [];

  return (
    <div className="post-orders-panel">
      <div className="section-header">
        <h3>Post orders</h3>
        <div className="header-actions">
          {canWriteOrders && (
            <Button variant="ghost" className="btn-sm" onClick={() => setDraftOpen(true)}>
              New draft
            </Button>
          )}
          {canPublish && (
            <Button variant="primary" className="btn-sm" onClick={() => setPublishOpen(true)}>
              Publish version
            </Button>
          )}
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && orders.length === 0 && (
        <p className="muted">No post orders yet.</p>
      )}

      {orders.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Version</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Effective</Table.HeaderCell>
              <Table.HeaderCell>Body</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {orders.map((o) => (
              <Table.Row key={o.id} className={o.status === 'active' ? 'row-active' : undefined}>
                <Table.Cell tone="strong">v{o.version}</Table.Cell>
                <Table.Cell>
                  <StatusBadge status={o.status}>{o.status}</StatusBadge>
                </Table.Cell>
                <Table.Cell>{fmtDate(o.effective_at)}</Table.Cell>
                <Table.Cell className="cell-truncate">{o.body ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {draftOpen && (
        <PostOrderModal
          postId={postId}
          mode="draft"
          onClose={() => setDraftOpen(false)}
        />
      )}
      {publishOpen && (
        <PostOrderModal
          postId={postId}
          mode="publish"
          onClose={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}

function PostOrderModal({
  postId,
  mode,
  onClose,
}: {
  postId: number;
  mode: 'draft' | 'publish';
  onClose: () => void;
}): JSX.Element {
  const [body, setBody] = useState('');
  const [effectiveAt, setEffectiveAt] = useState<number | null>(null);
  const [documentPath, setDocumentPath] = useState('');

  const createDraft = useCreatePostOrder();
  const publish = usePublishPostOrder();
  const mutation = mode === 'publish' ? publish : createDraft;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const input: CreatePostOrderInput = {};
    if (body.trim()) input.body = body.trim();
    if (effectiveAt !== null) input.effective_at = effectiveAt;
    if (documentPath.trim()) input.document_path = documentPath.trim();

    try {
      await mutation.mutateAsync({ postId, ...input });
      toast.success(mode === 'publish' ? 'Post order published.' : 'Draft created.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to save post order.');
    }
  };

  return (
    <Dialog
      title={mode === 'publish' ? 'Publish post order' : 'New draft post order'}
      open
      onClose={onClose}
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="post-order-form"
            variant="primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? 'Saving…'
              : mode === 'publish'
                ? 'Publish'
                : 'Create draft'}
          </Button>
        </>
      }
    >
      <form id="post-order-form" className="form-grid" onSubmit={onSubmit}>
        {mode === 'publish' && (
          <p className="form-hint form-field-full">
            Publishing archives the currently-active post order and creates a new
            active version.
          </p>
        )}
        <FormField label="Body" full>
          <Textarea
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Standing orders, escalation procedures, contact tree…"
          />
        </FormField>
        <FormField label="Effective date">
          <DateInput value={effectiveAt} onChange={setEffectiveAt} />
        </FormField>
        <FormField label="Document path">
          <Input
            value={documentPath}
            onChange={(e) => setDocumentPath(e.target.value)}
            placeholder="optional vault path"
          />
        </FormField>
      </form>
    </Dialog>
  );
}

function AddPostModal({
  siteId,
  onClose,
}: {
  siteId: number;
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [postType, setPostType] = useState<PostType>('gate');
  const [coverageHours, setCoverageHours] = useState('');
  const [headcount, setHeadcount] = useState('1');

  const create = useCreatePost();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const input: CreatePostInput = {
      name: name.trim(),
      post_type: postType,
    };
    if (coverageHours.trim()) input.coverage_hours = coverageHours.trim();
    const hc = Number(headcount);
    if (Number.isInteger(hc) && hc > 0) input.headcount_required = hc;

    try {
      await create.mutateAsync({ siteId, ...input });
      toast.success('Post added.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to add post.');
    }
  };

  return (
    <Dialog
      title="Add post"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-post-form"
            variant="primary"
            disabled={create.isPending || name.trim().length === 0}
          >
            {create.isPending ? 'Saving…' : 'Add'}
          </Button>
        </>
      }
    >
      <form id="add-post-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Type">
          <select
            className="form-input"
            value={postType}
            onChange={(e) => setPostType(e.target.value as PostType)}
          >
            {POST_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Coverage hours">
          <Input
            value={coverageHours}
            onChange={(e) => setCoverageHours(e.target.value)}
            placeholder="Mon-Fri 08:00-18:00"
          />
        </FormField>
        <FormField label="Headcount required">
          <Input
            type="number"
            value={headcount}
            onChange={(e) => setHeadcount(e.target.value)}
            min={1}
          />
        </FormField>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit site
// ---------------------------------------------------------------------------

function EditSiteModal({
  siteId,
  initial,
  onClose,
}: {
  siteId: number;
  initial: {
    name: string;
    address: string | null;
    state: string | null;
    lat: string | null;
    lng: string | null;
    geofence_radius_m: number;
    site_type: SiteType;
    status: SiteStatus;
    notes: string | null;
  };
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial.name);
  const [address, setAddress] = useState(initial.address ?? '');
  const [state, setState] = useState(initial.state ?? '');
  const [lat, setLat] = useState(initial.lat ?? '');
  const [lng, setLng] = useState(initial.lng ?? '');
  const [radius, setRadius] = useState(String(initial.geofence_radius_m));
  const [siteType, setSiteType] = useState<SiteType>(initial.site_type);
  const [status, setStatus] = useState<SiteStatus>(initial.status);
  const [notes, setNotes] = useState(initial.notes ?? '');

  const update = useUpdateSite();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const patch: UpdateSiteInput = {
      name: name.trim(),
      address: address.trim() || null,
      state: state || null,
      lat: lat.trim() || null,
      lng: lng.trim() || null,
      site_type: siteType,
      status,
      notes: notes.trim() || null,
    };
    const r = Number(radius);
    if (Number.isInteger(r) && r > 0) patch.geofence_radius_m = r;

    try {
      await update.mutateAsync({ id: siteId, ...patch });
      toast.success('Site updated.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update site.');
    }
  };

  return (
    <Dialog
      title="Edit site"
      open
      onClose={onClose}
      busy={update.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-site-form"
            variant="primary"
            disabled={update.isPending || name.trim().length === 0}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="edit-site-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Type">
          <select
            className="form-input"
            value={siteType}
            onChange={(e) => setSiteType(e.target.value as SiteType)}
          >
            {SITE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Status">
          <select
            className="form-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as SiteStatus)}
          >
            {SITE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Geofence radius (m)">
          <Input
            type="number"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            min={1}
          />
        </FormField>
        <FormField label="Address" full>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </FormField>
        <FormField label="State">
          <select
            className="form-input"
            value={state}
            onChange={(e) => setState(e.target.value)}
          >
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Latitude">
          <Input
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            inputMode="decimal"
          />
        </FormField>
        <FormField label="Longitude">
          <Input
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            inputMode="decimal"
          />
        </FormField>
        <FormField label="Notes" full>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormField>
      </form>
    </Dialog>
  );
}
